import type {
  EvaluatedCourseRef,
  ExamEvaluationCandidate,
  ExamEvaluations,
} from "../../../pages/App/HubSubida/hubTypes";
import type { GameWithTags } from "../../../services/gameQuiz/gameQuiz.types";
import type { CourseResolution } from "./quizCourseAssignment";

const sortedList = (ids: Iterable<number>): string =>
  Array.from(ids)
    .sort((a, b) => a - b)
    .join(", ");

const candidateLabel = ({ courseId, title }: ExamEvaluationCandidate): string =>
  `#${courseId} — ${title}`;

const comparableClave = (clave: string): string =>
  clave
    .trim()
    .toUpperCase()
    .replace(/^[PK](?=\d)/, "");

export const examCourseIds = (games: GameWithTags[]): number[] =>
  Array.from(
    new Set(
      games
        .map((game) => game.course_id)
        .filter((id): id is number => typeof id === "number"),
    ),
  );

export function pickExamEvaluation(
  found: ExamEvaluations,
  courseIds: number[],
): CourseResolution {
  const covering = found.evaluations.filter((evaluation) => {
    const evaluated = new Set(evaluation.evaluatedCourseIds);
    return courseIds.every((id) => evaluated.has(id));
  });
  if (covering.length === 1) {
    const [evaluation] = covering;
    return {
      ok: true,
      course: {
        id: evaluation.courseId,
        title: evaluation.title,
        clave: evaluation.clave,
      },
    };
  }
  if (covering.length > 1) {
    return {
      ok: false,
      error: `Hay más de una evaluación trimestral que evalúa todos los cursos del examen (${covering
        .map(candidateLabel)
        .join(", ")}); elige una en el formulario.`,
    };
  }
  const evaluatedAnywhere = new Set(
    found.evaluations.flatMap((evaluation) => evaluation.evaluatedCourseIds),
  );
  const missing = courseIds.filter((id) => !evaluatedAnywhere.has(id));
  const detail =
    missing.length > 0
      ? `Ninguna evaluación trimestral evalúa los cursos ${sortedList(missing)}.`
      : `Esos cursos están repartidos entre varias evaluaciones (${found.evaluations
          .map(candidateLabel)
          .join(", ")}).`;
  return {
    ok: false,
    error: `No hay una evaluación trimestral que evalúe todos los cursos del examen (${sortedList(courseIds)}). ${detail}`,
  };
}

export function examClaveErrors(
  games: GameWithTags[],
  questionClaves: string[],
  courses: EvaluatedCourseRef[],
): string[] {
  const byId = new Map(courses.map((course) => [course.id, course]));
  const reported = new Set<number>();
  return games.flatMap((game, index) => {
    const courseId = game.course_id;
    if (typeof courseId !== "number" || reported.has(courseId)) return [];
    const course = byId.get(courseId);
    if (!course) {
      reported.add(courseId);
      return [`Pregunta ${index + 1}: el curso ${courseId} no existe.`];
    }
    const clave = questionClaves[index] ?? "";
    if (
      !clave ||
      !course.clave ||
      comparableClave(clave) === comparableClave(course.clave)
    ) {
      return [];
    }
    reported.add(courseId);
    return [
      `Pregunta ${index + 1}: la \`clave\` «${clave}» no es la del curso ${courseId} (#${courseId} — ${course.title} tiene «${course.clave}»).`,
    ];
  });
}
